import { Node } from "@xyflow/react";
import { SearchResultData } from "../../app/slices/SearchResultsSlice";
import { SearchLookupContext } from "../../models/interactions/SearchableInteractionValues";
import type {
  SequenceCommentZone,
  SequenceNodeData,
  SequenceTextbox,
} from "../../models/interactions/SequenceModel";
import { getAdvancedSearchMatchingResults } from "../advanced_search_column/AdvancedSearchMatching";
import { StringUtils } from "../utils/StringUtils";
import { getInteractionFromFactory } from "../interaction_factory/InteractionFactory";
import { getSequenceNodeTitle } from "./SequenceNodeTitle";

export type SequenceNodeType = Node<SequenceNodeData, "sequence">;

export type SequenceNodeSearchMatch = {
  kind: "node";
  id: string;
  node: SequenceNodeType;
  title: string;
  results: SearchResultData[];
};

export type SequenceCommentZoneSearchMatch = {
  kind: "commentZone";
  id: string;
  commentZone: SequenceCommentZone;
  title: string;
  results: SearchResultData[];
};

export type SequenceTextboxSearchMatch = {
  kind: "textbox";
  id: string;
  textbox: SequenceTextbox;
  title: string;
  results: SearchResultData[];
};

export type SequenceCanvasSearchMatch =
  | SequenceNodeSearchMatch
  | SequenceCommentZoneSearchMatch
  | SequenceTextboxSearchMatch;

export function getSequenceNodeSearchResults(
  node: SequenceNodeType,
  query: string,
  getValue: (key: string) => string,
  searchLookupContext?: SearchLookupContext,
): SearchResultData[] {
  const { interaction } = node.data;
  const title = getSequenceNodeTitle(interaction);
  const factory = getInteractionFromFactory(interaction.type);
  const dedupedResults = new Map<string, SearchResultData>();

  const baseSearchableStrings =
    title && title !== interaction.name
      ? [title, interaction.name].filter(Boolean)
      : [interaction.name].filter(Boolean);

  getAdvancedSearchMatchingResults({
    searchableStrings: baseSearchableStrings,
    query,
    interaction,
    takeName: "",
    getValue,
    filterType: factory?.FilterType ?? "Sequence Node",
    objectIdPrefix: `${interaction.id}-base-`,
  }).forEach((result) => {
    dedupedResults.set(result.matchingText, result);
  });

  if (typeof factory?.Filter === "function") {
    factory.Filter(
      interaction,
      "",
      (searchableStrings, _takeName, sourceInteraction, filterType) =>
        getAdvancedSearchMatchingResults({
          searchableStrings,
          query,
          interaction: sourceInteraction,
          takeName: "",
          getValue,
          filterType,
        }),
      getValue,
      searchLookupContext,
    ).forEach((result) => {
      dedupedResults.set(result.matchingText, result);
    });
  }

  return Array.from(dedupedResults.values());
}

export function findSequenceNodeMatches(
  nodes: SequenceNodeType[],
  query: string,
  getValue: (key: string) => string,
  searchLookupContext?: SearchLookupContext,
): SequenceNodeSearchMatch[] {
  const trimmedQuery = query.trim();
  if (!trimmedQuery) {
    return [];
  }

  return nodes.reduce<SequenceNodeSearchMatch[]>((matches, node) => {
    const results = getSequenceNodeSearchResults(
      node,
      trimmedQuery,
      getValue,
      searchLookupContext,
    );
    if (results.length === 0) {
      return matches;
    }

    matches.push({
      kind: "node",
      id: node.id,
      node,
      title: getSequenceNodeTitle(node.data.interaction),
      results,
    });
    return matches;
  }, []);
}

function createCanvasTextMatchResult(
  id: string,
  matchingText: string,
  filterType: string,
): SearchResultData {
  return {
    objectId: id,
    matchingText,
    filterType,
  };
}

function getSequenceCommentZoneSearchResults(
  commentZone: SequenceCommentZone,
  query: string,
): SearchResultData[] {
  if (!StringUtils.IsMatching(commentZone.label, query)) {
    return [];
  }

  return [
    createCanvasTextMatchResult(commentZone.id, commentZone.label, "Comment Box"),
  ];
}

function getSequenceTextboxSearchResults(
  textbox: SequenceTextbox,
  query: string,
): SearchResultData[] {
  if (!StringUtils.IsMatching(textbox.text, query)) {
    return [];
  }

  return [createCanvasTextMatchResult(textbox.id, textbox.text, "Text Note")];
}

export function findSequenceCanvasMatches({
  nodes,
  commentZones,
  textboxes,
  query,
  getValue,
  searchLookupContext,
}: {
  nodes: SequenceNodeType[];
  commentZones: SequenceCommentZone[];
  textboxes: SequenceTextbox[];
  query: string;
  getValue: (key: string) => string;
  searchLookupContext?: SearchLookupContext;
}): SequenceCanvasSearchMatch[] {
  const trimmedQuery = query.trim();
  if (!trimmedQuery) {
    return [];
  }

  const nodeMatches = findSequenceNodeMatches(
    nodes,
    trimmedQuery,
    getValue,
    searchLookupContext,
  );
  const commentZoneMatches = commentZones.reduce<SequenceCommentZoneSearchMatch[]>(
    (matches, commentZone) => {
      const results = getSequenceCommentZoneSearchResults(commentZone, trimmedQuery);
      if (results.length === 0) {
        return matches;
      }

      matches.push({
        kind: "commentZone",
        id: commentZone.id,
        commentZone,
        title: commentZone.label,
        results,
      });
      return matches;
    },
    [],
  );
  const textboxMatches = textboxes.reduce<SequenceTextboxSearchMatch[]>(
    (matches, textbox) => {
      const results = getSequenceTextboxSearchResults(textbox, trimmedQuery);
      if (results.length === 0) {
        return matches;
      }

      matches.push({
        kind: "textbox",
        id: textbox.id,
        textbox,
        title: textbox.text,
        results,
      });
      return matches;
    },
    [],
  );

  return [...nodeMatches, ...commentZoneMatches, ...textboxMatches];
}
